/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  PearlUser,
  closeCorsErrorModal,
  oktaAuthStateChanged,
  selectCanViewCME,
  selectCorsErrorModalOpen,
  selectLogin,
} from '../sharedcomponents/Login/userSlice';
import { deleteSmartFhirCookieData, getSmartFhirCookieData } from '../sharedcomponents/Epic/smartFhirUtils';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CorsErrorModal from '../sharedcomponents/Login/CorsErrorModal';
import LoginErrorComponent from '../sharedcomponents/Login/LoginErrorComponent';
import { OKTA_ISSUER } from '../sharedcomponents/api/api_config';
import { fetchCMEPoints, selectCMEState } from '../PersonalArea/cmeSlice';
import styles from './TopLayout.module.scss';
import { useAuth } from '../sharedcomponents/Login/useHooks';
import { deleteShareVueCookieData, getShareVueCookieData } from '../sharedcomponents/ShareVue/ShareVueUtils';
import { isEnabledAffiliateAdminForUser } from '../sharedcomponents/permissions';
import TopMenu from '../sharedcomponents/TopMenu/TopMenu';

import CmeIcon, { AffiliateAdminIcon, BookmarkIcon, SearchIcon } from '../sharedcomponents/TopMenu/MenuIcons';
import { MessageBar } from '../sharedcomponents/chat/MessageBar';
import LinearProgress from '@mui/material/LinearProgress';
import { AFFILIATE_ADMIN, CME, CONSUMER_ADMIN, FAVORITES, SEARCH } from '../sharedcomponents/TopMenu/menuUtils';
import CircularProgress from '@mui/material/CircularProgress';

interface TopLayoutProps {
  pearlUserInfo?: PearlUser;
}

function TopLayout({ pearlUserInfo }: TopLayoutProps) {
  const { authState, logout } = useAuth();
  const dispatch = useAppDispatch();
  const corsErrorModalOpen = useAppSelector(selectCorsErrorModalOpen);
  const login = useAppSelector(selectLogin);
  const smartFhirCookieData = getSmartFhirCookieData();
  const shareVueCookieData = getShareVueCookieData();
  const { currentYearUnclaimed, loadingCMESummary } = useAppSelector(selectCMEState);

  const loggedout = !authState || !authState.isAuthenticated;

  const canViewCME = useAppSelector(selectCanViewCME);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (smartFhirCookieData || shareVueCookieData) return;
    void dispatch(oktaAuthStateChanged({ loggedout, pearlUserInfo }));
    // Note, do NOT make authState and oktaAuth dependencies,
    // despite what Okta's own documentation says.
    // Because these change, and resulted in us hitting the
    // okta server hundreds of times a second!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedout]); // Update if authState changes

  useEffect(() => {
    if (pearlUserInfo && canViewCME) {
      void dispatch(fetchCMEPoints());
    }
  }, [canViewCME, dispatch, pearlUserInfo]);
  const loginLayoutContainer = (
    <div className={styles.layoutContainer}>
      {!authState && (smartFhirCookieData?.error || shareVueCookieData?.error) ? (
        <Button
          onClick={() => {
            deleteSmartFhirCookieData();
            deleteShareVueCookieData();
          }}
        >
          Log in
        </Button>
      ) : !authState ? (
        <div className={styles.logindiv}>{/* <Loading variant="compact" /> */}</div>
      ) : !authState.isAuthenticated ? null /* we used to show a "log in' button here, but I removed because its never needed because we just log in automatically */ : !pearlUserInfo ? (
        /* We've logged in, but haven't fetched oktaUserInfo yet */
        <>
          {!login.errors ? <CircularProgress /> : null}
          <Button onClick={logout}>Log out</Button>
        </>
      ) : null}
    </div>
  );
  function handleCmeContainerClick(): void {
    navigate('/my-cme');
  }
  const canViewAdmin = useCallback(() => {
    return location.pathname === '/admin/logs';
  }, [location.pathname]);

  const cmeContainer = (
    <div className={styles.cmeContainer} onClick={handleCmeContainerClick}>
      <div className={styles.cmeTable}>
        <span className={styles.cmeLabel}>CME</span>
        <span className={styles.cmeValue}>
          {loadingCMESummary ? <LinearProgress /> : currentYearUnclaimed?.toFixed(2)}
        </span>
      </div>
    </div>
  );

  const topMenuItems = useMemo(() => {
    return [
      {
        title: SEARCH.label,
        view: true,
        route: SEARCH.route,
        icon: <SearchIcon />,
      },

      {
        title: CME.label,
        view: !!canViewCME,
        route: CME.route,
        icon: <CmeIcon />,
      },
      {
        title: FAVORITES.label,
        view: true,
        route: FAVORITES.route,
        icon: <BookmarkIcon />,
      },
      {
        title: AFFILIATE_ADMIN.label,
        view: isEnabledAffiliateAdminForUser(),
        route: AFFILIATE_ADMIN.route,
        icon: <AffiliateAdminIcon />,
      },
      { title: CONSUMER_ADMIN.label, view: canViewAdmin(), route: CONSUMER_ADMIN.route },
    ];
  }, [canViewCME, canViewAdmin]);

  return (
    <div className={styles.topLayoutWrapper}>
      {location.pathname.includes('chat') && <MessageBar />}
      <TopMenu topMenuItems={topMenuItems}>
        <CorsErrorModal
          open={corsErrorModalOpen}
          onClose={() => dispatch(closeCorsErrorModal())}
          okta_issuer={OKTA_ISSUER}
        />
        {loginLayoutContainer}
        {canViewCME && cmeContainer}
        <LoginErrorComponent />
      </TopMenu>
    </div>
  );
}

export default TopLayout;
